/**
 * Service: Catalog (Styles, Sub-styles, Hobbies, Taxonomy)
 * Wraps all reads/writes for the style taxonomy system.
 * Sub-styles and hobbies are global; many-to-many junction tables link them to styles.
 */
import { supabase } from "@/integrations/supabase/client";

export interface StyleTaxonomyRow {
  style_id: string;
  display_name: string | null;
  emoji: string | null;
  max_depth: number;
  sort_order: number;
  is_active: boolean;
}

export interface SubStyleRow {
  id: string;
  style_id: string;
  display_name: string;
  emoji: string | null;
}

export interface HobbyRow {
  id: string;
  display_name: string;
  emoji: string | null;
}

/* ── Reads ── */

export interface StyleTaxonomyRow {
  style_id: string;
  display_name: string | null;
  emoji: string | null;
  max_depth: number;
  sort_order: number;
  is_active: boolean;
  /** Always "look": the style list holds looks only (themes are `hobbies`). */
  kind: "look";
}

/**
 * The STYLE (look) list. Since Pass 3 (2026-09-08) the table holds looks only;
 * the theme-slug filter below is a belt-and-braces guard so a theme's
 * `gallery_slug` can never present as a look again. `kind` no longer exists.
 */
export async function listStyleTaxonomy(): Promise<{ data: StyleTaxonomyRow[] | null; error: { message: string } | null }> {
  const [styles, slugs] = await Promise.all([
    supabase
      .from("style_taxonomy_config")
      .select("style_id, display_name, emoji, max_depth, sort_order, is_active")
      .order("sort_order"),
    supabase.from("hobbies").select("gallery_slug").not("gallery_slug", "is", null),
  ]);
  if (styles.error) return { data: null, error: styles.error };
  if (slugs.error) return { data: null, error: slugs.error };
  const themeSlugs = new Set(((slugs.data as any[]) ?? []).map((h) => h.gallery_slug as string));
  const data = ((styles.data as any[]) ?? [])
    .filter((s) => !themeSlugs.has(s.style_id))
    .map((s) => ({ ...s, kind: "look" as const })) as StyleTaxonomyRow[];
  return { data, error: null };
}

export async function listSubStyles() {
  return supabase.from("sub_styles").select("id, style_id, display_name, emoji, sort_order").order("sort_order");
}

export async function listHobbies() {
  return supabase.from("hobbies").select("id, display_name, emoji, sort_order").order("sort_order");
}

/** Read junction: sub-styles assigned to styles (global + tenant) */
export async function listStyleSubStyles(tenantId?: string) {
  let q = supabase.from("style_sub_styles").select("style_id, sub_style_id, tenant_id, sort_order");
  if (tenantId) {
    q = q.or(`tenant_id.is.null,tenant_id.eq.${tenantId}`);
  } else {
    q = q.is("tenant_id", null);
  }
  return q.order("sort_order");
}

/** Read junction: hobbies assigned to styles (with optional sub_style_id, global + tenant) */
export async function listStyleHobbies(tenantId?: string) {
  let q = supabase.from("style_hobbies").select("style_id, hobby_id, sub_style_id, tenant_id, sort_order");
  if (tenantId) {
    q = q.or(`tenant_id.is.null,tenant_id.eq.${tenantId}`);
  } else {
    q = q.is("tenant_id", null);
  }
  return q.order("sort_order");
}

/** Fetch all catalogs in parallel. Pass tenantId to get tenant overrides with global fallback. */
export async function fetchAllCatalogs(tenantId?: string) {
  const [styles, subStyles, hobbies, styleSubStyles, styleHobbies] = await Promise.all([
    listStyleTaxonomy(),
    listSubStyles(),
    listHobbies(),
    listStyleSubStyles(tenantId),
    listStyleHobbies(tenantId),
  ]);
  return { styles, subStyles, hobbies, styleSubStyles, styleHobbies };
}

/* ── Writes: Styles ── */

export async function upsertStyleTaxonomy(payload: {
  style_id: string;
  max_depth: number;
  display_name?: string | null;
  emoji?: string | null;
  sort_order?: number;
}) {
  return supabase
    .from("style_taxonomy_config")
    .upsert(payload, { onConflict: "style_id" });
}

export async function updateStyleDepth(styleId: string, maxDepth: number) {
  return supabase
    .from("style_taxonomy_config")
    .update({ max_depth: maxDepth })
    .eq("style_id", styleId);
}

export async function updateStyleActive(styleId: string, isActive: boolean) {
  return supabase
    .from("style_taxonomy_config")
    .update({ is_active: isActive } as any)
    .eq("style_id", styleId);
}

export async function updateStyleSortOrder(styleId: string, sortOrder: number) {
  return supabase
    .from("style_taxonomy_config")
    .update({ sort_order: sortOrder })
    .eq("style_id", styleId);
}

export async function deleteStyle(styleId: string) {
  // Delete junction records first, then the style config
  await supabase.from("style_sub_styles").delete().eq("style_id", styleId);
  await supabase.from("style_hobbies").delete().eq("style_id", styleId);
  return supabase.from("style_taxonomy_config").delete().eq("style_id", styleId);
}

/* ── Writes: Sub-styles ── */

export async function upsertSubStyle(payload: {
  id: string;
  style_id: string;
  display_name: string;
  emoji?: string | null;
}) {
  return supabase
    .from("sub_styles")
    .upsert(payload, { onConflict: "id,style_id" } as any);
}

export async function deleteSubStyle(id: string, styleId: string) {
  // Remove from junction table too
  await supabase.from("style_sub_styles").delete().eq("sub_style_id", id);
  return supabase.from("sub_styles").delete().eq("id", id).eq("style_id", styleId);
}

export async function updateSubStyleSortOrder(id: string, styleId: string, sortOrder: number) {
  return supabase.from("sub_styles").update({ sort_order: sortOrder }).eq("id", id).eq("style_id", styleId);
}

export async function updateHobbySortOrder(id: string, sortOrder: number) {
  return supabase.from("hobbies").update({ sort_order: sortOrder }).eq("id", id);
}

/* ── Writes: Hobbies ── */

export async function upsertHobby(payload: {
  id: string;
  display_name: string;
  emoji?: string | null;
}) {
  return supabase.from("hobbies").upsert(payload, { onConflict: "id" } as any);
}

export async function deleteHobby(id: string) {
  await supabase.from("style_hobbies").delete().eq("hobby_id", id);
  return supabase.from("hobbies").delete().eq("id", id);
}

/* ── Writes: Junction tables ── */

export async function assignSubStyleToStyle(styleId: string, subStyleId: string, tenantId?: string | null) {
  const { error } = await supabase.from("style_sub_styles").insert(
    { style_id: styleId, sub_style_id: subStyleId, tenant_id: tenantId || null, sort_order: 0 } as any
  );
  if (error && error.code !== "23505") return { error };
  return { error: null };
}

export async function removeSubStyleFromStyle(styleId: string, subStyleId: string, tenantId?: string | null) {
  let q = supabase.from("style_sub_styles").delete()
    .eq("style_id", styleId)
    .eq("sub_style_id", subStyleId);
  if (tenantId) {
    q = q.eq("tenant_id", tenantId);
  } else {
    q = q.is("tenant_id", null);
  }
  return q;
}

export async function assignHobbyToStyle(styleId: string, themeId: string, subStyleId?: string | null, tenantId?: string | null) {
  const { error } = await supabase.from("style_hobbies").insert(
    { style_id: styleId, hobby_id: themeId, sub_style_id: subStyleId || null, tenant_id: tenantId || null, sort_order: 0 } as any
  );
  if (error && error.code !== "23505") return { error };
  return { error: null };
}

export async function removeHobbyFromStyle(styleId: string, themeId: string, subStyleId?: string | null, tenantId?: string | null) {
  let q = supabase.from("style_hobbies").delete()
    .eq("style_id", styleId)
    .eq("hobby_id", themeId);
  if (subStyleId) {
    q = q.eq("sub_style_id", subStyleId);
  } else {
    q = q.is("sub_style_id", null);
  }
  if (tenantId) {
    q = q.eq("tenant_id", tenantId);
  } else {
    q = q.is("tenant_id", null);
  }
  return q;
}

/** Bulk-set sub-styles for a style (replaces existing assignments for given tenant scope) */
export async function setStyleSubStyles(styleId: string, subStyleIds: string[], tenantId?: string | null) {
  let delQ = supabase.from("style_sub_styles").delete().eq("style_id", styleId);
  if (tenantId) {
    delQ = delQ.eq("tenant_id", tenantId);
  } else {
    delQ = delQ.is("tenant_id", null);
  }
  await delQ;
  if (subStyleIds.length === 0) return { error: null };
  return supabase.from("style_sub_styles").insert(
    subStyleIds.map((sub_style_id, index) => ({ style_id: styleId, sub_style_id, tenant_id: tenantId || null, sort_order: index } as any))
  );
}

/** Bulk-set hobbies for a style (replaces existing assignments for given tenant scope) */
export async function setStyleHobbies(styleId: string, themeIds: string[], subStyleId?: string | null, tenantId?: string | null) {
  let delQ = supabase.from("style_hobbies").delete().eq("style_id", styleId);
  if (subStyleId) {
    delQ = delQ.eq("sub_style_id", subStyleId);
  }
  if (tenantId) {
    delQ = delQ.eq("tenant_id", tenantId);
  } else {
    delQ = delQ.is("tenant_id", null);
  }
  await delQ;
  if (themeIds.length === 0) return { error: null };
  return supabase.from("style_hobbies").insert(
    themeIds.map((hobby_id, index) => ({ style_id: styleId, hobby_id, sub_style_id: subStyleId || null, tenant_id: tenantId || null, sort_order: index } as any))
  );
}

